import { useState } from "react";
import { useGameStore } from "../../store/gameStore";
import { useUIStore } from "../../store/uiStore";
import { useApi } from "../../hooks/useApi";
import { hapticFeedback, hapticNotification } from "../../telegram/init";

export function WorkersPanel() {
  const closePanel = useUIStore((s) => s.closePanel);
  const cities = useGameStore((s) => s.cities);
  const activeCityId = useGameStore((s) => s.activeCityId);
  const updateCity = useGameStore((s) => s.updateCity);
  const api = useApi();

  const city = cities.find((c) => c.id === activeCityId);
  if (!city) return null;

  const maxWorkers = Math.floor(city.populationCurrent);
  const [forest, setForest] = useState(city.forestWorkers);
  const [mine, setMine] = useState(city.mineWorkers);
  const [scientists, setScientists] = useState(city.scientists);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idle = maxWorkers - forest - mine - scientists;
  const hasAcademy = city.buildings.some((b) => b.buildingType === "ACADEMY" && b.level > 0);

  async function handleSave() {
    if (!activeCityId) return;
    setLoading(true);
    setError(null);
    try {
      await api.assignWorkers(activeCityId, forest, mine, scientists);
      hapticNotification("success");
      const cityData = await api.getCity(activeCityId);
      updateCity({ id: activeCityId, ...cityData });
      closePanel();
    } catch (e: any) {
      setError(e.message);
      hapticNotification("error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={closePanel} />
      <div className="panel-sheet">
        <div className="panel-handle" />
        <h2>Assign Citizens</h2>

        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "var(--space-lg)",
          marginBottom: "var(--space-xl)",
          padding: "var(--space-md)",
          background: "var(--parchment)",
          borderRadius: "var(--radius)",
        }}>
          <StatBadge label="Population" value={`${maxWorkers}`} icon={"\u{1F465}"} />
          <StatBadge
            label="Idle"
            value={`${idle}`}
            icon={"\u{1F4B0}"}
            color={idle < 0 ? "var(--terra)" : "var(--olive)"}
          />
          <StatBadge label="Happiness" value={`${Math.floor(city.happiness)}`} icon={"\u{1F60A}"} />
        </div>

        {idle < 0 && (
          <div style={{
            marginBottom: "var(--space-lg)",
            padding: "var(--space-md)",
            background: "rgba(196, 91, 58, 0.1)",
            border: "1px solid rgba(196, 91, 58, 0.3)",
            borderRadius: "var(--radius)",
            color: "var(--terra-dark)",
            fontSize: "0.85rem",
            textAlign: "center",
          }}>
            Too many workers assigned! Remove {Math.abs(idle)} to continue.
          </div>
        )}

        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-lg)",
          marginBottom: "var(--space-xl)",
        }}>
          <WorkerSlider
            icon={"\u{1F332}"}
            label="Woodcutters"
            sublabel="Gather wood from the forest"
            value={forest}
            max={maxWorkers}
            onChange={(v) => { setForest(v); hapticFeedback("light"); }}
          />
          <WorkerSlider
            icon={"\u26CF\uFE0F"}
            label="Miners"
            sublabel={`Mine ${city.islandLuxury?.toLowerCase() ?? "luxury resource"}`}
            value={mine}
            max={maxWorkers}
            onChange={(v) => { setMine(v); hapticFeedback("light"); }}
          />
          {hasAcademy && (
            <WorkerSlider
              icon={"\u{1F4DA}"}
              label="Scientists"
              sublabel="Research at the Academy"
              value={scientists}
              max={maxWorkers}
              onChange={(v) => { setScientists(v); hapticFeedback("light"); }}
            />
          )}
        </div>

        <button
          className="btn-primary"
          disabled={loading || idle < 0}
          onClick={handleSave}
          style={{ width: "100%", padding: "14px" }}
        >
          {loading ? "Saving..." : "Assign Workers"}
        </button>

        {error && (
          <div style={{
            marginTop: "var(--space-md)",
            padding: "var(--space-md)",
            background: "rgba(196, 91, 58, 0.1)",
            borderRadius: "var(--radius)",
            color: "var(--terra-dark)",
            fontSize: "0.85rem",
            textAlign: "center",
          }}>
            {error}
          </div>
        )}
      </div>
    </>
  );
}

function StatBadge({ label, value, icon, color }: {
  label: string; value: string; icon: string; color?: string;
}) {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "2px",
    }}>
      <span style={{ fontSize: "1rem" }}>{icon}</span>
      <span style={{
        fontFamily: "var(--font-display)",
        fontSize: "1rem",
        fontWeight: 700,
        color: color ?? "var(--ink)",
      }}>
        {value}
      </span>
      <span style={{
        fontFamily: "var(--font-display)",
        fontSize: "0.55rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "var(--ink-faded)",
      }}>
        {label}
      </span>
    </div>
  );
}

function WorkerSlider({ icon, label, sublabel, value, max, onChange }: {
  icon: string;
  label: string;
  sublabel: string;
  value: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "var(--space-xs)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
          <span style={{ fontSize: "1.1rem" }}>{icon}</span>
          <div>
            <div style={{
              fontFamily: "var(--font-display)",
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "var(--ink)",
            }}>
              {label}
            </div>
            <div style={{ fontSize: "0.7rem", color: "var(--ink-faded)" }}>
              {sublabel}
            </div>
          </div>
        </div>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-sm)",
        }}>
          <button
            onClick={() => onChange(Math.max(0, value - 1))}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "var(--parchment)",
              border: "1px solid var(--sand)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1rem",
              color: "var(--ink)",
            }}
          >
            -
          </button>
          <span style={{
            fontFamily: "var(--font-display)",
            fontSize: "1rem",
            fontWeight: 700,
            minWidth: "28px",
            textAlign: "center",
            color: "var(--terra-dark)",
          }}>
            {value}
          </span>
          <button
            onClick={() => onChange(Math.min(max, value + 1))}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "var(--parchment)",
              border: "1px solid var(--sand)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1rem",
              color: "var(--ink)",
            }}
          >
            +
          </button>
        </div>
      </div>

      {/* Slider track */}
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        style={{
          width: "100%",
          height: "6px",
          appearance: "none",
          WebkitAppearance: "none",
          background: `linear-gradient(to right, var(--terra) 0%, var(--terra) ${(value / max) * 100}%, var(--sand) ${(value / max) * 100}%, var(--sand) 100%)`,
          borderRadius: "3px",
          outline: "none",
          cursor: "pointer",
        }}
      />
    </div>
  );
}
